import React from "react";
import {
  Box,
  TextField,
  Grid,
  Typography,
  useTheme,
  InputAdornment,
} from "@mui/material";
import SectionSubheading from "./SectionSubheading";

// Field configuration array
const AMBIENT_SURFACE_FIELDS = [
  {
    id: "time",
    label: "Time (Date/Hours)",
    type: "text",
    required: false,
  },
  {
    id: "dryBulb",
    label: "Dry Bulb",
    type: "number",
    required: true,
    unit: "°C",
  },
  {
    id: "wetBulb",
    label: "Wet Bulb",
    type: "number",
    required: true,
    unit: "°C",
  },
  {
    id: "humidity",
    label: "Humidity",
    type: "number",
    required: true,
    unit: "%",
  },
  {
    id: "dewPoint",
    label: "Dew Point",
    type: "number",
    required: true,
    unit: "°C",
  },
  {
    id: "substrateTemp",
    label: "Substrate Temp",
    type: "number",
    required: true,
    unit: "°C",
  },
  {
    id: "substrateDewPoint",
    label: "Substrate Temp  (Minus) Dew Point",
    type: "number",
    required: true,
    unit: "°C",
  },
  {
    id: "status",
    label: "Status",
    type: "text",
    required: false,
    disabled: true,
  },
  {
    id: "finalInspection",
    label: "Final Inspection",
    type: "textarea",
    required: true,
  },
];

// FieldBox component moved outside to prevent re-creation on each render
const FieldBox = ({
  field,
  values,
  errors,
  touched,
  setFieldValue,
  viewOnly,
  styles,
  theme,
  stageType,
}) => {
  const fieldErrors = errors?.ambientCondition || {};
  const fieldTouched = touched?.ambientCondition || {};
  const hasError = fieldErrors[field.id] && fieldTouched[field.id];
  const isTextarea = field.type === "textarea";
  const isTimeField = field.id === "time";
  const isCheckbox = field.type === "checkbox";
  const isStatusField = field.id === "status";

  // Set default value for status field based on stage type
  const fieldValue =
    isStatusField && !values?.ambientCondition?.[field.id]
      ? stageType === "blasting"
        ? "Blast"
        : "Paint"
      : values?.ambientCondition?.[field.id] || "";

  return (
    <Grid
      size={{
        xs: isTextarea ? 12 : 6,
        sm: isTextarea ? 12 : 6,
        md: isTextarea ? 12 : 6,
      }}
    >
      <Box
        sx={{
          ...styles.fieldRow,
          alignItems: isCheckbox ? "center" : "flex-start",
        }}
      >
        <Typography variant="body2" sx={styles.label}>
          {field.label}
          {field.required && (
            <Box
              component="span"
              sx={{
                color: theme.palette.error.main,
                ml: 0.5,
                fontSize: "1rem",
              }}
            >
              *
            </Box>
          )}
        </Typography>
        <TextField
          type={isTextarea ? undefined : field.type}
          value={fieldValue}
          onChange={(e) =>
            setFieldValue(`ambientCondition.${field.id}`, e.target.value)
          }
          disabled={viewOnly || isTimeField || field.disabled}
          size="small"
          error={hasError}
          helperText={hasError ? fieldErrors[field.id] : ""}
          multiline={isTextarea}
          minRows={isTextarea ? 2 : undefined}
          InputProps={{
            ...(field.unit && {
              endAdornment: (
                <InputAdornment position="end">
                  <Box
                    sx={{
                      fontSize: "0.875rem",
                      color: theme.palette.text.secondary,
                      fontWeight: 500,
                    }}
                  >
                    {field.unit}
                  </Box>
                </InputAdornment>
              ),
            }),
          }}
          sx={styles.textField}
          inputProps={{
            ...(field.type === "number" && {
              step: field.id === "humidity" ? 0.1 : "any",
              min: field.id === "humidity" ? 0 : undefined,
              max: field.id === "humidity" ? 100 : undefined,
            }),
          }}
        />
      </Box>
    </Grid>
  );
};

/**
 * AmbientSurfaceConditionTable - Reusable table component for ambient and surface condition data
 * @param {Object} values - Form values object containing the ambient condition data
 * @param {Object} errors - Form errors object
 * @param {Object} touched - Form touched fields object
 * @param {Function} setFieldValue - Formik setFieldValue function
 * @param {boolean} viewOnly - Whether the form is in view-only mode
 * @param {string} stageType - Stage type: "blasting" or "painting" (determines default status value)
 */
const AmbientSurfaceConditionTable = ({
  values,
  errors,
  touched,
  setFieldValue,
  viewOnly = false,
  stageType = "painting",
}) => {
  const theme = useTheme();
  // Styles following WorkOrderForm pattern
  const styles = {
    fieldRow: {
      display: "flex",
      mb: 2,
    },
    label: {
      width: { xs: 130, md: 160 },
      flexShrink: 0,
      color: theme.palette.text.primary,
      fontWeight: 500,
      fontSize: "0.875rem",
      mr: 2,
    },
    textField: {
      width: "100%",
      minWidth: 150,
      // maxWidth: 400,
      "& .MuiOutlinedInput-root": {
        backgroundColor: "transparent",
      },
      "& .MuiInputBase-input": {
        fontSize: "0.875rem",
      },
      "& .MuiFormHelperText-root": {
        fontSize: "0.75rem",
        mt: 0.5,
      },
    },
  };

  return (
    <Box sx={{ mt: 2 }}>
      <SectionSubheading title="Ambient & Surface Condition" />
      <Box sx={{ overflowX: "auto", width: "100%" }}>
        <Grid container spacing={2} sx={{ mt: 2, minWidth: 670 }}>
          {AMBIENT_SURFACE_FIELDS.map((field) => (
            <FieldBox
              key={field.id}
              field={field}
              values={values}
              errors={errors}
              touched={touched}
              setFieldValue={setFieldValue}
              viewOnly={viewOnly}
              styles={styles}
              theme={theme}
              stageType={stageType}
            />
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default AmbientSurfaceConditionTable;
